import React, { useState, useEffect, useRef } from 'react';
import { MemoryRouter } from 'react-router-dom';
import L from 'leaflet';
import { getBrokerSession, createBroker, brokerLogin, brokerLogout } from '../brokerStore';
import BrokerCommunity from '../BrokerCommunity';
import AlertWatcher from '../AlertWatcher';
import { requestShare, leaveShare, codeForDevice } from '../carCode';
import { brokerLinkStatus, brokerTrackingEnabled } from '../brokerLink';
import { getAllDevices, getPositions } from '../traccar';
import { odoMilesFromAttrs, getActiveRental, isRentalVehicle } from '../rentalStore';
import RentalPanel from '../RentalPanel';

// Broker Community is temporarily hidden — set to true to bring it back.
const COMMUNITY_ENABLED = false;
const dName = (d) => (d.attributes || {}).displayName || d.name;

// ---- futuristic theme: injected once (keyframes + glass/neon utilities) ----
function FxStyles() {
  return (
    <style>{`
      @keyframes fxdrift { 0%{background-position:0 0} 100%{background-position:70px 70px} }
      @keyframes fxfloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-9px)} }
      @keyframes fxpulse { 0%,100%{opacity:.45} 50%{opacity:1} }
      .fx-root{ font-family:'Poppins',system-ui,sans-serif; }
      .fx-grid{ background-image:linear-gradient(rgba(34,211,238,.055) 1px,transparent 1px),linear-gradient(90deg,rgba(168,85,247,.06) 1px,transparent 1px); background-size:36px 36px; animation:fxdrift 14s linear infinite; }
      .fx-glass{ background:rgba(255,255,255,.045); -webkit-backdrop-filter:blur(14px); backdrop-filter:blur(14px); border:1px solid rgba(255,255,255,.10); }
      .fx-neonbtn{ background:linear-gradient(135deg,#22d3ee,#a855f7); color:#04121a; font-weight:800; border:none; border-radius:12px; cursor:pointer; box-shadow:0 0 22px rgba(34,211,238,.35); transition:box-shadow .2s,transform .1s; }
      .fx-neonbtn:hover{ box-shadow:0 0 32px rgba(168,85,247,.6); transform:translateY(-1px); }
      .fx-ghostbtn{ background:rgba(255,255,255,.06); color:#cbd5e1; border:1px solid rgba(255,255,255,.16); border-radius:10px; cursor:pointer; font-weight:700; }
      .fx-ghostbtn:hover{ border-color:#22d3ee; color:#e2f5ff; }
      .fx-input{ width:100%; box-sizing:border-box; padding:12px 13px; border-radius:11px; background:rgba(255,255,255,.04); border:1px solid rgba(255,255,255,.14); color:#e2e8f0; font-size:14px; outline:none; transition:border-color .2s,box-shadow .2s; }
      .fx-input:focus{ border-color:#22d3ee; box-shadow:0 0 0 3px rgba(34,211,238,.16); }
      .fx-input::placeholder{ color:#64748b; }
      .fx-lbl{ font-size:11px; font-weight:800; letter-spacing:.6px; color:#7dd3fc; text-transform:uppercase; display:block; margin-bottom:5px; }
      .fx-grad{ background:linear-gradient(120deg,#67e8f9,#a855f7 55%,#f472b6); -webkit-background-clip:text; background-clip:text; color:transparent; }
      .fx-card{ border-radius:16px; }
      .fx-glow{ position:absolute; border-radius:50%; filter:blur(50px); opacity:.5; animation:fxpulse 6s ease-in-out infinite; }
    `}</style>
  );
}

export default function BrokerPage() {
  const [session, setSession] = useState(getBrokerSession());
  if (!session) return <BrokerAuth onDone={() => setSession(getBrokerSession())} />;
  return <BrokerConsole broker={session} onSignOut={() => { brokerLogout(); setSession(null); }} />;
}

function Feature({ icon, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '11px', marginBottom: '13px' }}>
      <span style={{ width: '30px', height: '30px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '9px', background: 'rgba(34,211,238,.12)', border: '1px solid rgba(34,211,238,.3)', fontSize: '15px' }}>{icon}</span>
      <span style={{ fontSize: '13.5px', color: 'rgba(226,232,240,.9)', lineHeight: 1.45, paddingTop: '4px' }}>{children}</span>
    </div>
  );
}

function BrokerAuth({ onDone }) {
  const [mode, setMode] = useState('login');
  const [f, setF] = useState({ name: '', company: '', email: '', password: '' });
  const [err, setErr] = useState('');
  const submit = async () => {
    try { if (mode === 'signup') await createBroker(f); else await brokerLogin(f.email, f.password); setErr(''); onDone(); }
    catch (e) { setErr(e.message); }
  };
  return (
    <div className="fx-root" style={{ position: 'relative', minHeight: '100vh', background: '#05070f', color: '#e2e8f0', overflow: 'hidden' }}>
      <FxStyles />
      {/* background video */}
      <video autoPlay muted loop playsInline src="/broker-login.mp4" style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }} />
      <div style={{ position: 'fixed', inset: 0, zIndex: 1, background: 'linear-gradient(180deg, rgba(5,7,15,.55), rgba(5,7,15,.30) 42%, rgba(5,7,15,.85))' }} />

      <div style={{ position: 'relative', zIndex: 2, minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', gap: '26px' }}>
        <div className="fx-glass" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', borderRadius: '999px', padding: '6px 14px', fontSize: '11px', fontWeight: 800, letterSpacing: '1.5px', color: '#7dd3fc' }}>◆ TAGALONG · BROKER OS</div>

        {/* SIGN-IN — on top, translucent so the video shows through */}
        <div className="fx-card" style={{ padding: '26px', width: 'min(410px, 94vw)', background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.22)', boxShadow: '0 30px 80px rgba(0,0,0,.45), inset 0 1px 0 rgba(255,255,255,.14)', backdropFilter: 'blur(10px) saturate(120%)', WebkitBackdropFilter: 'blur(10px) saturate(120%)' }}>
          <div style={{ fontWeight: 800, fontSize: '22px', marginBottom: '3px' }}>{mode === 'signup' ? 'Create account' : 'Welcome back'}</div>
          <div style={{ fontSize: '13px', color: '#aebbd0', marginBottom: '20px' }}>{mode === 'signup' ? 'Spin up your broker console.' : 'Sign in to your broker console.'}</div>
          {mode === 'signup' && (
            <>
              <div style={{ marginBottom: '13px' }}><span className="fx-lbl">Your name</span><input className="fx-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
              <div style={{ marginBottom: '13px' }}><span className="fx-lbl">Company (optional)</span><input className="fx-input" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} /></div>
            </>
          )}
          <div style={{ marginBottom: '13px' }}><span className="fx-lbl">Email</span><input className="fx-input" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} /></div>
          <div style={{ marginBottom: '18px' }}><span className="fx-lbl">Password</span><input className="fx-input" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} /></div>
          {err && <div style={{ color: '#fda4af', fontSize: '12px', marginBottom: '12px', fontWeight: 600 }}>{err}</div>}
          <button onClick={submit} className="fx-neonbtn" style={{ width: '100%', padding: '13px', fontSize: '15px' }}>{mode === 'signup' ? 'Create account ▸' : 'Sign in ▸'}</button>
          <div style={{ fontSize: '13px', color: '#aebbd0', marginTop: '16px', textAlign: 'center' }}>
            {mode === 'signup' ? 'Already have an account? ' : 'New here? '}
            <button onClick={() => { setErr(''); setMode(mode === 'signup' ? 'login' : 'signup'); }} style={{ background: 'transparent', border: 'none', color: '#67e8f9', fontWeight: 800, cursor: 'pointer' }}>{mode === 'signup' ? 'Sign in' : 'Create one'}</button>
          </div>
        </div>

        {/* WORDING — on the bottom: what TagAlong is + the opportunity */}
        <div style={{ width: 'min(640px, 94vw)', textAlign: 'center' }}>
          <h1 style={{ fontSize: '27px', lineHeight: 1.15, fontWeight: 800, margin: '0 0 10px', letterSpacing: '-0.5px' }}>Run a rental fleet <span className="fx-grad">without owning the cars.</span></h1>
          <p style={{ fontSize: '14px', color: 'rgba(226,232,240,.88)', lineHeight: 1.55, margin: '0 auto 18px', maxWidth: '540px' }}>TagAlong turns owners' idle cars into your fleet. Scan an owner's code and their car is yours to rent out — you run the rental, they earn, you earn. No lot, no fleet to buy, no overhead.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: '8px 18px', textAlign: 'left', maxWidth: '580px', margin: '0 auto' }}>
            <Feature icon="🔑">Add any owner's car with a share code or QR</Feature>
            <Feature icon="🪪">Digital contracts — license, photos & e-signature</Feature>
            <Feature icon="💳">Deposits & payments — cash or instant pay link</Feature>
            <Feature icon="🛰️">Live GPS mileage, toll capture & PDF invoices</Feature>
          </div>
          <div className="fx-glass" style={{ display: 'inline-block', marginTop: '14px', borderRadius: '999px', padding: '8px 18px', fontSize: '13px', fontWeight: 700, color: '#a5f3fc', border: '1px solid rgba(34,211,238,.35)' }}>💸 Earn on every rental — start with a single code.</div>
          <div style={{ fontSize: '12px', color: '#7c8aa3', marginTop: '16px' }}><a href="/car?tagalong" style={{ color: '#7c8aa3', textDecoration: 'none' }}>‹ Back to TagAlong</a></div>
        </div>
      </div>
    </div>
  );
}

// Camera QR scanner — uses the native BarcodeDetector when available, else jsQR
// from CDN. Extracts the car code from the scanned link (?add=CODE) or raw text.
function QrScanner({ onClose, onCode }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let raf; let detector; let cancelled = false;
    const canvas = document.createElement('canvas');
    const loadJsQR = () => new Promise((res) => {
      if (window.jsQR) return res();
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jsQR/1.4.0/jsQR.min.js';
      s.onload = res; s.onerror = res; document.head.appendChild(s);
    });
    const stop = () => { if (raf) cancelAnimationFrame(raf); if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); };
    const handle = (text) => {
      if (!text || cancelled) return; cancelled = true; stop();
      let code = text;
      try { const u = new URL(text); code = u.searchParams.get('add') || text; } catch { /* not a url */ }
      onCode(code);
    };
    const scan = async () => {
      const v = videoRef.current;
      if (v && v.videoWidth) {
        if (detector) {
          try { const codes = await detector.detect(v); if (codes && codes[0]) return handle(codes[0].rawValue); } catch { /* ignore */ }
        } else if (window.jsQR) {
          canvas.width = v.videoWidth; canvas.height = v.videoHeight;
          const ctx = canvas.getContext('2d'); ctx.drawImage(v, 0, 0);
          const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const r = window.jsQR(img.data, img.width, img.height); if (r && r.data) return handle(r.data);
        }
      }
      if (!cancelled) raf = requestAnimationFrame(scan);
    };
    const start = async () => {
      try {
        streamRef.current = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        if (videoRef.current) { videoRef.current.srcObject = streamRef.current; await videoRef.current.play().catch(() => {}); }
      } catch { setErr('Could not open the camera. Check permissions, or type the code instead.'); return; }
      if ('BarcodeDetector' in window) { try { detector = new window.BarcodeDetector({ formats: ['qr_code'] }); } catch { /* ignore */ } }
      if (!detector) await loadJsQR();
      scan();
    };
    start();
    return () => { cancelled = true; stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 4000, background: 'rgba(0,0,0,0.92)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px', padding: '20px' }}>
      <div style={{ color: '#67e8f9', fontWeight: 800, fontSize: '15px' }}>Scan the car's QR code</div>
      <div style={{ position: 'relative', width: 'min(360px,86vw)', aspectRatio: '1', borderRadius: '18px', overflow: 'hidden', background: '#000', border: '2px solid rgba(34,211,238,.6)', boxShadow: '0 0 40px rgba(34,211,238,.35)' }}>
        <video ref={videoRef} playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: '16%', border: '2px solid rgba(255,255,255,.5)', borderRadius: '14px' }} />
      </div>
      {err && <div style={{ color: '#fda4af', fontSize: '13px', maxWidth: '320px', textAlign: 'center' }}>{err}</div>}
      <button onClick={onClose} style={{ background: 'rgba(255,255,255,.14)', color: '#e2e8f0', border: '1px solid rgba(255,255,255,.25)', borderRadius: '10px', padding: '10px 22px', fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
    </div>
  );
}

// Live map of a car the broker is actively renting. Recenters as positions poll.
function BrokerLiveTrack({ device, position }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const mkRef = useRef(null);
  useEffect(() => {
    if (!elRef.current || !position || position.latitude == null) return undefined;
    const map = L.map(elRef.current, { zoomControl: true, attributionControl: false }).setView([position.latitude, position.longitude], 15);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(map);
    const icon = L.divIcon({ className: '', iconSize: [26, 26], iconAnchor: [13, 13], html: '<div style="font-size:22px;filter:drop-shadow(0 0 6px rgba(34,211,238,.7))">📍</div>' });
    mkRef.current = L.marker([position.latitude, position.longitude], { icon }).addTo(map);
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 160);
    return () => map.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (mapRef.current && mkRef.current && position && position.latitude != null) {
      const ll = [position.latitude, position.longitude];
      mkRef.current.setLatLng(ll); mapRef.current.panTo(ll);
    }
  }, [position && position.latitude, position && position.longitude]);

  const mph = position ? Math.round((position.speed || 0) * 1.15078) : 0;
  return (
    <div className="fx-glass fx-card" style={{ overflow: 'hidden', marginBottom: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px' }}>
        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22d3ee', boxShadow: '0 0 10px #22d3ee' }} />
        <div style={{ fontWeight: 800, fontSize: '13px', flex: 1 }}>{dName(device)} <span style={{ color: '#64748b', fontWeight: 600, fontSize: '11px' }}>· renting now</span></div>
        <span style={{ fontSize: '11px', color: '#7dd3fc' }}>{mph} mph{position && position.fixTime ? ` · ${new Date(position.fixTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}</span>
      </div>
      {position && position.latitude != null
        ? <div ref={elRef} style={{ height: '220px', width: '100%', background: '#0b1220' }} />
        : <div style={{ height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontSize: '12px' }}>Waiting for the car's location…</div>}
    </div>
  );
}

function BrokerConsole({ broker, onSignOut }) {
  const [devices, setDevices] = useState([]);
  const [positions, setPositions] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [communityOpen, setCommunityOpen] = useState(false);
  const openRef = useRef(null); openRef.current = openId; // pause auto-refresh while managing a car
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState('');
  const [scanning, setScanning] = useState(false);
  const [, force] = useState(0);
  const refresh = () => force((n) => n + 1);

  const load = async () => {
    try { const [d, p] = await Promise.all([getAllDevices(), getPositions()]); setDevices(d); setPositions(p); } catch { /* ignore */ }
  };
  useEffect(() => { load(); const t = setInterval(() => { if (!openRef.current) load(); }, 20000); return () => clearInterval(t); }, []);

  const doAdd = async (c) => {
    setMsg('◇ Resolving code…');
    let r;
    try { r = await requestShare(c); } catch (e) { setMsg(e && e.notFound ? `No car found for “${c}”. Confirm the code with the owner.` : 'Could not send the request — try again.'); return; }
    if (r.status === 'approved') { setMsg(`${r.name} is already in your fleet.`); return; }
    if (r.status === 'pending') { setMsg(`Request for ${r.name} is already waiting on the owner's approval.`); return; }
    setCode(''); setMsg(`📨 Request sent to the owner of ${r.name}. It appears in your fleet once they approve.`);
    load();
  };

  useEffect(() => {
    const u = new URLSearchParams(window.location.search);
    const add = u.get('add');
    if (add) {
      doAdd(add);
      u.delete('add');
      const qs = u.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const myCars = devices.filter((d) => brokerLinkStatus(d, broker.id) === 'approved');
  const pendingCars = devices.filter((d) => brokerLinkStatus(d, broker.id) === 'pending');
  const posFor = (id) => positions.find((p) => p.deviceId === id);
  // Live tracking + alerts are available WHILE the broker has an active rental,
  // AND only if the owner hasn't turned tracking off for this broker.
  const activeRentalCars = myCars.filter((d) => getActiveRental(d.id, `b:${broker.id}`));
  const trackableCars = activeRentalCars.filter((d) => brokerTrackingEnabled(d, broker.id));
  const activeIds = trackableCars.map((d) => d.id);
  const activeCount = activeRentalCars.length;

  return (
    <div className="fx-root fx-grid" style={{ minHeight: '100vh', background: 'radial-gradient(1000px 600px at 90% -10%, rgba(168,85,247,.18), transparent 55%), radial-gradient(900px 500px at -10% 110%, rgba(34,211,238,.14), transparent 55%), #05070f', color: '#e2e8f0' }}>
      <FxStyles />
      {/* alerts — only for cars the broker is actively renting */}
      {activeIds.length > 0 && <MemoryRouter><AlertWatcher deviceIds={activeIds} /></MemoryRouter>}
      <div style={{ maxWidth: '920px', margin: '0 auto', padding: '22px' }}>
        {/* header */}
        <div className="fx-glass fx-card" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 18px', marginBottom: '16px' }}>
          <div style={{ width: '38px', height: '38px', borderRadius: '11px', background: 'linear-gradient(135deg,#22d3ee,#a855f7)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', boxShadow: '0 0 18px rgba(34,211,238,.45)' }}>🚗</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: '16px' }}>Broker <span className="fx-grad">Console</span></div>
            <div style={{ fontSize: '11px', color: '#7dd3fc', letterSpacing: '.5px' }}>{(broker.name || broker.email).toUpperCase()}{broker.company ? ` · ${broker.company}` : ''}</div>
          </div>
          <button onClick={onSignOut} className="fx-ghostbtn" style={{ padding: '8px 14px', fontSize: '12px' }}>Sign out</button>
        </div>

        {/* top tabs — Rentals vs Community. Community is temporarily hidden;
            flip COMMUNITY_ENABLED to true to bring it back. */}
        {COMMUNITY_ENABLED && (
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <button onClick={() => setCommunityOpen(false)} className={communityOpen ? 'fx-ghostbtn' : 'fx-neonbtn'} style={{ padding: '9px 18px', fontSize: '13px', fontWeight: 800 }}>🚗 Rentals</button>
            <button onClick={() => setCommunityOpen(true)} className={communityOpen ? 'fx-neonbtn' : 'fx-ghostbtn'} style={{ padding: '9px 18px', fontSize: '13px', fontWeight: 800 }}>🌐 Community</button>
          </div>
        )}

        {COMMUNITY_ENABLED && communityOpen && <BrokerCommunity broker={broker} />}

        {!communityOpen && (<>
        {/* stat strip */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '12px', marginBottom: '16px' }}>
          {[['FLEET', myCars.length, '#22d3ee'], ['ACTIVE RENTALS', activeCount, '#a855f7'], ['AVAILABLE', myCars.length - activeCount, '#f472b6']].map(([k, v, c]) => (
            <div key={k} className="fx-glass fx-card" style={{ padding: '14px 16px' }}>
              <div style={{ fontSize: '10px', fontWeight: 800, letterSpacing: '.8px', color: '#94a3b8' }}>{k}</div>
              <div style={{ fontSize: '28px', fontWeight: 800, color: c, textShadow: `0 0 18px ${c}66` }}>{v}</div>
            </div>
          ))}
        </div>

        {/* LIVE tracking — only while a rental is active AND the owner allows it */}
        {trackableCars.length > 0 && (
          <>
            <div style={{ fontSize: '11px', fontWeight: 800, letterSpacing: '.8px', color: '#a5f3fc', margin: '4px 4px 8px' }}>🛰️ LIVE TRACKING — ACTIVE RENTALS</div>
            {trackableCars.map((d) => <BrokerLiveTrack key={d.id} device={d} position={posFor(d.id)} />)}
          </>
        )}
        {activeRentalCars.filter((d) => !brokerTrackingEnabled(d, broker.id)).map((d) => (
          <div key={d.id} className="fx-glass fx-card" style={{ padding: '12px 14px', marginBottom: '10px', color: '#94a3b8', fontSize: '12px' }}>🔕 Live tracking for <b style={{ color: '#e2e8f0' }}>{dName(d)}</b> is turned off by the owner.</div>
        ))}

        {/* add a car */}
        <div className="fx-glass fx-card" style={{ padding: '16px', marginBottom: '16px' }}>
          <div style={{ fontWeight: 800, fontSize: '14px', marginBottom: '3px' }}>➕ Link a car to your fleet</div>
          <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '11px' }}>Enter the car's share code — each car has its own, so the owner picks exactly which to send (or scan its QR).</div>
          <form onSubmit={(e) => { e.preventDefault(); doAdd(code); }} style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <input className="fx-input" enterKeyHint="go" autoCapitalize="characters" style={{ flex: 1, minWidth: '150px', fontFamily: 'monospace', letterSpacing: '1px' }} placeholder="TA-XXXXXXX" value={code} onChange={(e) => setCode(e.target.value)} />
            <button type="submit" className="fx-neonbtn" style={{ padding: '11px 20px' }}>⏎ Enter</button>
            <button type="button" onClick={() => setScanning(true)} className="fx-ghostbtn" style={{ padding: '11px 16px' }}>📷 Scan QR</button>
          </form>
          {msg && <div style={{ fontSize: '12px', color: '#a5f3fc', marginTop: '9px' }}>{msg}</div>}
          {scanning && <QrScanner onClose={() => setScanning(false)} onCode={(c) => { setScanning(false); doAdd(c); }} />}
        </div>

        {/* pending owner approval */}
        {pendingCars.length > 0 && (
          <>
            <div style={{ fontSize: '11px', fontWeight: 800, letterSpacing: '.8px', color: '#fbbf24', margin: '4px 4px 8px' }}>PENDING OWNER APPROVAL</div>
            {pendingCars.map((d) => (
              <div key={d.id} className="fx-glass fx-card" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '13px 16px', marginBottom: '10px', border: '1px solid rgba(251,191,36,.3)' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#fbbf24', boxShadow: '0 0 10px #fbbf24' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 800, fontSize: '14px' }}>{dName(d)}</div>
                  <div style={{ fontSize: '11px', color: '#64748b', fontFamily: 'monospace' }}>{codeForDevice(d)}</div>
                </div>
                <span style={{ fontSize: '9px', fontWeight: 800, padding: '3px 9px', borderRadius: '999px', background: 'rgba(251,191,36,.14)', color: '#fcd34d', border: '1px solid rgba(251,191,36,.4)' }}>WAITING</span>
                <button onClick={async () => { await leaveShare(d.id).catch(() => {}); load(); }} className="fx-ghostbtn" style={{ padding: '6px 12px', fontSize: '12px' }}>Cancel</button>
              </div>
            ))}
          </>
        )}

        {/* fleet */}
        <div style={{ fontSize: '11px', fontWeight: 800, letterSpacing: '.8px', color: '#7dd3fc', margin: '4px 4px 8px' }}>YOUR FLEET</div>
        {myCars.length === 0 && (
          <div className="fx-glass fx-card" style={{ padding: '26px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>Fleet empty. Link a car with its share code above.</div>
        )}
        {myCars.map((d) => {
          const active = getActiveRental(d.id, `b:${broker.id}`);
          const isSel = openId === d.id;
          // owner turned rentals OFF for this car → broker can't start new rentals
          const ownerOff = (d.attributes || {}).rentalEnabled === false && !active;
          return (
            <div key={d.id} className="fx-glass fx-card" style={{ marginBottom: '10px', overflow: 'hidden', boxShadow: isSel ? '0 0 26px rgba(34,211,238,.25)' : 'none', opacity: ownerOff ? 0.75 : 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '13px 16px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: ownerOff ? '#f43f5e' : active ? '#a855f7' : '#22d3ee', boxShadow: `0 0 10px ${ownerOff ? '#f43f5e' : active ? '#a855f7' : '#22d3ee'}` }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 800, fontSize: '14px' }}>{dName(d)}</div>
                  <div style={{ fontSize: '11px', color: '#64748b', fontFamily: 'monospace', letterSpacing: '.5px' }}>{codeForDevice(d)}</div>
                </div>
                {active ? <span style={{ fontSize: '9px', fontWeight: 800, padding: '3px 9px', borderRadius: '999px', background: 'rgba(168,85,247,.16)', color: '#c4b5fd', border: '1px solid rgba(168,85,247,.4)' }}>ACTIVE</span>
                  : ownerOff ? <span style={{ fontSize: '9px', fontWeight: 800, padding: '3px 9px', borderRadius: '999px', background: 'rgba(244,63,94,.14)', color: '#fda4af', border: '1px solid rgba(244,63,94,.4)' }}>🔒 UNAVAILABLE</span>
                    : isRentalVehicle(d.id) ? <span style={{ fontSize: '9px', fontWeight: 800, padding: '3px 9px', borderRadius: '999px', background: 'rgba(34,211,238,.14)', color: '#67e8f9', border: '1px solid rgba(34,211,238,.4)' }}>RENTAL</span> : null}
                {ownerOff
                  ? <span style={{ fontSize: '11px', color: '#fda4af', fontWeight: 700, padding: '7px 10px' }}>Unavailable by owner</span>
                  : <button onClick={() => setOpenId(isSel ? null : d.id)} className="fx-neonbtn" style={{ padding: '7px 14px', fontSize: '12px' }}>{isSel ? 'Close' : 'Manage ▸'}</button>}
                <button onClick={async () => { if (window.confirm('Unlink this car from your fleet?')) { await leaveShare(d.id).catch(() => {}); if (isSel) setOpenId(null); load(); } }} title="Unlink" className="fx-ghostbtn" style={{ padding: '6px 9px', fontSize: '13px' }}>✕</button>
              </div>
              {ownerOff && (
                <div style={{ padding: '10px 16px', borderTop: '1px solid rgba(255,255,255,.08)', fontSize: '11.5px', color: '#94a3b8' }}>
                  The owner has turned off rentals for this car, so you can’t start a new rental. It stays in your fleet — you’ll be able to rent it again if the owner turns rentals back on.
                </div>
              )}
              {isSel && !ownerOff && (
                <div style={{ padding: '12px', borderTop: '1px solid rgba(255,255,255,.08)', background: '#05070f' }}>
                  <div style={{ background: '#cbd5e1', borderRadius: '14px', padding: '12px' }}>
                    <RentalPanel
                      deviceId={d.id}
                      actor={`b:${broker.id}`}
                      odometerMi={odoMilesFromAttrs((posFor(d.id) || {}).attributes || {})}
                      vehicle={{ name: dName(d), year: (d.attributes || {}).vehYear, make: (d.attributes || {}).vehMake, model: (d.attributes || {}).vehModel, plate: (d.attributes || {}).vehPlate, vin: ((posFor(d.id) || {}).attributes || {}).vin }}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
        <div style={{ textAlign: 'center', fontSize: '10px', color: '#334155', marginTop: '20px', letterSpacing: '1px' }}>TAGALONG BROKER OS · v1</div>
        </>)}
      </div>
    </div>
  );
}
